import React, { useCallback, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Box, Typography, useRightSideBar } from '@p81-common/ui';
import { MspChildAssociationPayload, MspChildWizard } from '../MspChildWizard';
import {
  MspChildOverrideWizard,
  MspChildOverridePayload
} from '../MspChildOverrideWizard';
import { CredentialsFormValues } from '../shared/CredentialsStep';
import { CompletionScreen } from '../shared/CompletionScreen';
import {
  ValidationResultScreen,
  buildValidationResultLabels
} from '../shared/ValidationResultScreen';
import { ModeSelectScreen, ModeOption } from '../shared/ModeSelectScreen';
import { createClasses } from '../../styles';
import { buildMigrationLogsUrl } from '../../helpers/buildMigrationLogsUrl';
import {
  useAssociationOutcome,
  nothingStarted
} from '../../helpers/useAssociationOutcome';
import { runCreateUnderParentAssociation } from '../../helpers/runCreateUnderParentAssociation';
import { runMspChildOverride } from '../MspChildOverrideWizard/helpers/runMspChildOverride';
import { useRegion } from 'contexts/RegionContext';
import AssociateTenants from '../../AssociateTenants';

type MspChildMode =
  | 'CREATE_UNDER_PARENT'
  | 'ASSOCIATE_EXISTING'
  | 'ASSOCIATE_ONLY';
type Stage =
  | 'select'
  | 'create-under-parent'
  | 'msp-child-migration-started'
  | 'associate-existing'
  | 'msp-child-override-started'
  | 'associate-only'
  | 'associate-only-done'
  // Pre-flow validation skipped the tenant, or accepted it alongside failures.
  | 'validation-result';

const PRESCREEN = 'tenantsAssociation.mspChildPreScreen';
const WIZARD = 'tenantsAssociation.mspChildWizard';
const MIGRATION_STARTED =
  'tenantsAssociation.mspChildWizard.migrationStartedStep';

const initialCredentials: CredentialsFormValues = {
  parentUuid: '',
  uuidConfirmed: false,
  clientId: '',
  secretKey: '',
  adminEmail: ''
};

interface MspChildModeSelectProps {
  tenantId: string;
  companyName: string;
  updateDataFn(): void;
}

const MspChildModeSelect = ({
  tenantId,
  companyName,
  updateDataFn
}: MspChildModeSelectProps) => {
  const { t } = useTranslation('activity');
  const classes = createClasses();
  const { setOpenSecondary, clearComponentsArray } = useRightSideBar();
  // The migration runs in the region the operator is currently viewing, so the
  // logs land in that region's Datadog org.
  const { region } = useRegion();
  const [mode, setMode] = useState<MspChildMode | null>(null);
  // The right sidebar framework keeps an internal stack of its open
  // components. The top X button pops the stack instead of closing the
  // sidebar - so a stack longer than 1 makes X go back instead of close.
  // We keep the stack at length 1 in two ways:
  //   1. Sub-flows (wizard, migration screen) render inline below based
  //      on `stage`, instead of being pushed onto the stack via the
  //      framework's API.
  //   2. closeSidebar calls clearComponentsArray() so the stack doesn't
  //      survive a close - otherwise the next open would add a second
  //      copy and the stack would grow past 1.
  const [stage, setStage] = useState<Stage>('select');
  // Wizard state lives here (not inside MspChildWizard) so that the
  // operator's typed credentials survive a Previous → re-select cycle.
  const [credentials, setCredentials] =
    useState<CredentialsFormValues>(initialCredentials);
  const [scheduledConfirmed, setScheduledConfirmed] = useState(false);
  const [isAssociating, setIsAssociating] = useState(false);
  const [isOverriding, setIsOverriding] = useState(false);
  // UUID entered in the Associate Only flow - shown on its success screen.
  const [associatedUuid, setAssociatedUuid] = useState('');
  // Portal child UUID the operator typed in the override flow. Echoed back on
  // the success screen so they can confirm they targeted the right tenant.
  const [overrideChildUuid, setOverrideChildUuid] = useState('');
  const {
    migrationBatchId,
    upfrontErrors,
    validationResult,
    clearUpfrontErrors,
    handleOutcome
  } = useAssociationOutcome(t);
  // Which wizard step is showing - drives the per-step page title.
  const [wizardStep, setWizardStep] = useState(0);

  const closeSidebar = useCallback(() => {
    setOpenSecondary(false);
    clearComponentsArray();
  }, [setOpenSecondary, clearComponentsArray]);

  const handleNext = useCallback(() => {
    setWizardStep(0);
    if (mode === 'CREATE_UNDER_PARENT') {
      setStage('create-under-parent');
    } else if (mode === 'ASSOCIATE_EXISTING') {
      setStage('associate-existing');
    } else if (mode === 'ASSOCIATE_ONLY') {
      setStage('associate-only');
    }
  }, [mode]);

  const handleBackToSelect = useCallback(() => {
    setStage('select');
    clearUpfrontErrors();
    setCredentials(prev => ({ ...prev, uuidConfirmed: false }));
  }, [clearUpfrontErrors]);

  const handleBackFromOverride = useCallback(() => {
    setStage('select');
    clearUpfrontErrors();
  }, [clearUpfrontErrors]);

  // Leaving the confirm step drops the failures it was showing. Without this
  // the operator corrects the field, returns, and is still looking at the box
  // citing the rule they just fixed.
  const handleStepChange = useCallback(
    (step: number) => {
      setWizardStep(step);
      clearUpfrontErrors();
    },
    [clearUpfrontErrors]
  );

  // Must be referentially stable: AssociateTenants folds onAssociated into
  // the effect that sets the bottom action bar, and an inline lambda here
  // re-triggers that effect on every context render - an infinite loop.
  const handleAssociateOnlyDone = useCallback((uuid: string) => {
    setAssociatedUuid(uuid);
    setStage('associate-only-done');
  }, []);

  const handleFinishAssociation = useCallback(
    async (payload: MspChildAssociationPayload) => {
      setIsAssociating(true);
      const outcome = await runCreateUnderParentAssociation({
        tenantId,
        credentials: payload.credentials
      });
      setIsAssociating(false);

      const { screen, batchStarted } = handleOutcome(outcome, WIZARD);
      if (screen === 'started') setStage('msp-child-migration-started');
      if (screen === 'validation-result') setStage('validation-result');
      if (batchStarted) {
        setCredentials(initialCredentials);
        updateDataFn();
      }
    },
    [tenantId, handleOutcome, updateDataFn]
  );

  const handleSubmitOverride = useCallback(
    async (payload: MspChildOverridePayload) => {
      setIsOverriding(true);
      const outcome = await runMspChildOverride({ tenantId, ...payload });
      setIsOverriding(false);

      const { screen, batchStarted } = handleOutcome(
        outcome,
        `${WIZARD}.override`
      );
      if (screen === 'started') setStage('msp-child-override-started');
      if (screen === 'validation-result') setStage('validation-result');
      if (batchStarted) {
        setOverrideChildUuid(payload.credentials.accountUuid);
        setMode(null);
        updateDataFn();
      }
    },
    [tenantId, handleOutcome, updateDataFn]
  );

  const options = useMemo<ModeOption<MspChildMode>[]>(
    () => [
      {
        value: 'CREATE_UNDER_PARENT',
        label: t(`${PRESCREEN}.OPTION_CREATE_LABEL`),
        description: t(`${PRESCREEN}.OPTION_CREATE_DESC`)
      },
      {
        value: 'ASSOCIATE_EXISTING',
        label: t(`${PRESCREEN}.OPTION_ASSOCIATE_LABEL`),
        description: t(`${PRESCREEN}.OPTION_ASSOCIATE_DESC`)
      },
      {
        value: 'ASSOCIATE_ONLY',
        label: t(`${PRESCREEN}.OPTION_ASSOCIATE_ONLY_LABEL`),
        description: t(`${PRESCREEN}.OPTION_ASSOCIATE_ONLY_DESC`)
      }
    ],
    [t]
  );

  // The confirm step of both wizards swaps the page title per the Figma spec.
  const isConfirmStep =
    (stage === 'create-under-parent' || stage === 'associate-existing') &&
    wizardStep === 1;
  // Not "success": the batch runs in the background long after the 202, so none
  // of these stages means the migration finished - only that it was accepted.
  const isInProgressStage =
    stage === 'msp-child-migration-started' ||
    stage === 'msp-child-override-started' ||
    stage === 'associate-only-done' ||
    (stage === 'validation-result' &&
      !!validationResult &&
      !nothingStarted(validationResult));

  const titleKey =
    stage === 'validation-result' &&
    validationResult &&
    nothingStarted(validationResult)
      ? 'activity:tenantsAssociation.TITLE_NOT_STARTED'
      : isInProgressStage
      ? 'activity:tenantsAssociation.TITLE_IN_PROGRESS'
      : isConfirmStep
      ? 'activity:tenantsAssociation.TITLE_CONFIRM'
      : 'activity:tenantsAssociation.TITLE';

  // Both migration success screens end the same way - a link into this batch's
  // Datadog logs, which is the only place the per-tenant progress shows up.
  // They differ in their copy keys and in what their body interpolates.
  const renderMigrationStarted = (
    keyPrefix: string,
    values?: Record<string, string>
  ) => (
    <CompletionScreen
      title={t(`${keyPrefix}.TITLE`)}
      body={t(`${keyPrefix}.BODY`, { companyName, ...values })}
      actionLabel={t(`${keyPrefix}.VIEW_LOGS`)}
      actionHref={buildMigrationLogsUrl(region.code, migrationBatchId)}
    />
  );

  const renderStage = () => {
    if (stage === 'validation-result' && validationResult) {
      const { tenants, validCount, invalidCount } = validationResult;
      const allRejected = nothingStarted(validationResult);
      return (
        <ValidationResultScreen
          labels={buildValidationResultLabels(t, { allRejected })}
          tenants={tenants}
          validCount={validCount}
          invalidCount={invalidCount}
          batchId={migrationBatchId}
          // Nothing started when everything was rejected, so there are no logs
          // to open - the CTA closes the drawer instead.
          {...(allRejected
            ? { onAction: closeSidebar }
            : {
                actionHref: buildMigrationLogsUrl(region.code, migrationBatchId)
              })}
        />
      );
    }

    if (stage === 'create-under-parent') {
      return (
        <MspChildWizard
          upfrontErrors={upfrontErrors}
          credentials={credentials}
          onCredentialsChange={setCredentials}
          scheduledConfirmed={scheduledConfirmed}
          onScheduledConfirmedChange={setScheduledConfirmed}
          onBack={handleBackToSelect}
          onFinish={handleFinishAssociation}
          isFinishing={isAssociating}
          onStepChange={handleStepChange}
        />
      );
    }

    if (stage === 'msp-child-migration-started') {
      return renderMigrationStarted(MIGRATION_STARTED);
    }

    if (stage === 'associate-existing') {
      return (
        <MspChildOverrideWizard
          upfrontErrors={upfrontErrors}
          onBack={handleBackFromOverride}
          onFinish={handleSubmitOverride}
          isFinishing={isOverriding}
          onStepChange={handleStepChange}
        />
      );
    }

    if (stage === 'msp-child-override-started') {
      // The override flow is the one where the operator typed a portal UUID, so
      // its screen echoes that UUID back for confirmation.
      return renderMigrationStarted(`${WIZARD}.override.completeStep`, {
        tenantUUID: overrideChildUuid
      });
    }

    if (stage === 'associate-only') {
      return (
        <AssociateTenants
          companyName={companyName}
          tenantId={tenantId}
          updateDataFn={updateDataFn}
          onBack={handleBackToSelect}
          onAssociated={handleAssociateOnlyDone}
        />
      );
    }

    if (stage === 'associate-only-done') {
      return (
        <CompletionScreen
          title={t('tenantsAssociation.successScreen.TITLE')}
          body={t('tenantsAssociation.successScreen.BODY', {
            tenantUUID: associatedUuid
          })}
          actionLabel={t('tenantsAssociation.successScreen.DONE')}
          onAction={closeSidebar}
        />
      );
    }

    return (
      <ModeSelectScreen
        subtitle={t(`${PRESCREEN}.PAGE_SUBTITLE`)}
        warningTitle={t(`${PRESCREEN}.WARNING_TITLE`)}
        sectionTitle={t(`${PRESCREEN}.SECTION_TITLE`)}
        options={options}
        value={mode}
        onChange={setMode}
        radioName="msp-child-mode"
        nextLabel={t(`${PRESCREEN}.NEXT`)}
        cancelLabel={t('buttons.CANCEL')}
        onNext={handleNext}
        onCancel={closeSidebar}
      />
    );
  };

  return (
    <Box>
      <Typography variant="h2" className={classes.heading}>
        <Trans
          i18nKey={titleKey}
          values={{ companyName }}
          components={{ span: <span /> }}
        />
      </Typography>
      {renderStage()}
    </Box>
  );
};

export default MspChildModeSelect;
